(() => {
  const GLOBAL_CLASS_KEY = 'SizeGuideModal';
  const GLOBAL_INSTANCE_KEY = 'sizeGuideModalInstance';

  const initInstance = () => {
    const ModalClass = window[GLOBAL_CLASS_KEY];
    if (!ModalClass) {
      return;
    }

    if (window[GLOBAL_INSTANCE_KEY]) {
      if (typeof window[GLOBAL_INSTANCE_KEY].refresh === 'function') {
        window[GLOBAL_INSTANCE_KEY].refresh();
      }
      return;
    }

    window[GLOBAL_INSTANCE_KEY] = new ModalClass();
  };

  if (window[GLOBAL_CLASS_KEY]) {
    initInstance();
    return;
  }

  class SizeGuideModal {
    constructor() {
      this.handleCloseButtonClick = () => this.close();
      this.handleBackdropClick = () => this.close();
      this.handleEscKey = (event) => {
        if (!this.isOpen()) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      };
      this.handleOpenEvent = (event) => {
        if (event.detail) {
          this.handleOpen(event.detail);
        }
      };

      this.documentEventsBound = false;
      this.boundCloseBtn = null;
      this.boundBackdrop = null;
      this.lastFocusedElement = null;
      this.lastTriggerSelector = null;
      this.addToCartAfterSelection = false;
      this.addToBundleAfterSelection = false;
      this.isAddingToCartAfterSelection = false;
      this.closeAfterSelectionDelayMs = 150;

      this.assignElements();
      this.bindEvents();
    }

    static escapeCssValue(value) {
      if (typeof value !== 'string') return '';
      if (window.CSS && typeof window.CSS.escape === 'function') {
        return window.CSS.escape(value);
      }

      return value.replace(/["\\]/g, '\\$&');
    }

    assignElements() {
      this.modal = document.getElementById('size-guide-modal');
      this.closeBtn = this.modal?.querySelector('.size-guide-modal__close') || null;
      this.backdrop = this.modal?.querySelector('.size-guide-modal__backdrop') || null;
      this.content = this.modal?.querySelector('.size-guide-modal__content') || null;
    }

    bindEvents() {
      if (!this.modal) {
        return;
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.handleCloseButtonClick);
        this.boundCloseBtn = this.closeBtn;
      }

      if (this.backdrop) {
        this.backdrop.addEventListener('click', this.handleBackdropClick);
        this.boundBackdrop = this.backdrop;
      }

      if (!this.documentEventsBound) {
        document.addEventListener('keydown', this.handleEscKey);
        document.addEventListener('size-guide:open', this.handleOpenEvent);
        this.documentEventsBound = true;
      }
    }

    unbindElementEvents() {
      if (this.boundCloseBtn) {
        this.boundCloseBtn.removeEventListener('click', this.handleCloseButtonClick);
        this.boundCloseBtn = null;
      }

      if (this.boundBackdrop) {
        this.boundBackdrop.removeEventListener('click', this.handleBackdropClick);
        this.boundBackdrop = null;
      }
    }

    refresh() {
      this.unbindElementEvents();
      this.assignElements();
      this.bindEvents();
      this.detectedProductType = null;
    }

    destroy() {
      this.unbindElementEvents();

      if (this.documentEventsBound) {
        document.removeEventListener('keydown', this.handleEscKey);
        document.removeEventListener('size-guide:open', this.handleOpenEvent);
        this.documentEventsBound = false;
      }
    }

    handleOpen(data) {
      if (!this.modal || !this.content) {
        this.refresh();
      }

      if (!this.modal || !this.content) {
        return;
      }

      const {
        popupType,
        contentType,
        pageId,
        imageId,
        sizeChartTitle,
        sizeSelectTitle,
        sizeOptionName,
        sizeConversionEnabled,
        sizeConversionType,
        sizeConversionRules,
        cardContext,
        productId,
        cardWrapper,
        button
      } = data;

      const triggerButton = button instanceof HTMLElement ? button : null;
      this.lastFocusedElement =
        triggerButton ||
        (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      this.lastTriggerSelector = this.buildTriggerSelector(triggerButton);

      this.currentSizeOptionName = sizeOptionName;
      this.addToCartAfterSelection = triggerButton?.dataset.addToCartAfterSelection === 'true';
      this.addToBundleAfterSelection = triggerButton?.dataset.addToBundleAfterSelection === 'true';
      this.sizeConversionEnabled = sizeConversionEnabled === 'true';
      this.sizeConversionType = sizeConversionType;
      this.sizeConversionRules = sizeConversionRules;
      this.detectedProductType = null;
      this.cardWrapper = cardWrapper || null;

      const title = this.modal.querySelector('.size-guide-modal__title');
      if (title) {
        if (popupType === 'size_select') {
          title.textContent = sizeSelectTitle || 'SELECT SIZE';
        } else if (popupType === 'size_chart') {
          title.textContent = sizeChartTitle || 'Size Guide';
        } else {
          title.textContent = 'Size Guide';
        }
      }

      if (popupType === 'size_chart') {
        this.loadSizeChart(contentType, pageId, imageId);
      } else if (popupType === 'size_select') {
        if (cardContext) {
          this.modal.classList.add('size-guide-modal--card-variants');
          this.loadCardSizeSelect(cardWrapper, sizeOptionName, productId);
        } else {
          this.modal.classList.remove('size-guide-modal--card-variants');
          this.loadSizeSelect();
        }
      }

      this.open();
    }

    buildTriggerSelector(triggerButton) {
      if (!(triggerButton instanceof HTMLElement)) {
        return null;
      }

      if (triggerButton.classList.contains('variant-picker__size-guide-btn')) {
        const popupType = triggerButton.dataset.sizePopupType;
        const optionName = triggerButton.dataset.sizeOptionName;
        if (!popupType || !optionName) {
          return '.variant-picker__size-guide-btn';
        }

        const escapedPopupType = SizeGuideModal.escapeCssValue(popupType);
        const escapedOptionName = SizeGuideModal.escapeCssValue(optionName);
        return `.variant-picker__size-guide-btn[data-size-popup-type="${escapedPopupType}"][data-size-option-name="${escapedOptionName}"]`;
      }

      if (triggerButton.classList.contains('card-variant-popup-btn')) {
        const productId = triggerButton.dataset.productId;
        if (!productId) {
          return '.card-variant-popup-btn';
        }
        const escapedProductId = SizeGuideModal.escapeCssValue(productId);
        return `.card-variant-popup-btn[data-product-id="${escapedProductId}"]`;
      }

      return null;
    }

    async loadSizeChart(contentType, pageId, imageId) {
      if (!this.content) {
        return;
      }

      this.content.innerHTML = '<div class="loading">Loading...</div>';

      if (contentType === 'page' && pageId) {
        try {
          const response = await fetch(`/pages/${pageId}`);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const pageContent = doc.querySelector('.page-content') || doc.querySelector('main');
          this.content.innerHTML = pageContent ? pageContent.innerHTML : '<p>Page content not found</p>';
        } catch (error) {
          this.content.innerHTML = '<p>Error loading size chart page</p>';
        }
        return;
      }

      if ((contentType === 'image' || !contentType) && imageId) {
        const imageUrl = imageId;

        this.content.innerHTML = `
          <div class="size-chart-image">
            <img src="${imageUrl}" alt="Size chart" style="width: 100%; height: auto; max-width: 100%;">
          </div>
        `;

        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = 'Size chart';
        img.style.cssText = 'width: 100%; height: auto; max-width: 100%;';

        img.onload = () => {
          this.content.innerHTML = `
            <div class="size-chart-image">
              ${img.outerHTML}
            </div>
          `;
        };

        img.onerror = () => {
          this.content.innerHTML = `
            <div class="size-chart-image">
              <p>Upload an image to display it here</p>
            </div>
          `;
        };

        return;
      }

      this.content.innerHTML = '<p>No content configured for size chart</p>';
    }

    loadSizeSelect() {
      if (!this.content) {
        return;
      }

      const sizeFieldset = this.findSizeFieldset();
      if (!sizeFieldset) {
        this.content.innerHTML = `
          <div class="size-select">
            <p>No size options found for this product.</p>
          </div>
        `;
        return;
      }

      const optionsContainer = sizeFieldset.querySelector('.variant-picker__options');
      if (!optionsContainer) {
        this.content.innerHTML = `
          <div class="size-select">
            <p>No size options container found.</p>
          </div>
        `;
        return;
      }

      const optionsContent = optionsContainer.cloneNode(true);
      this.isolateModalVariantInputs(optionsContent);

      let conversionHeader = '';
      if (this.sizeConversionEnabled) {
        conversionHeader = `
          <div class="size-conversion-header">
            <div class="size-conversion-labels">
              <span class="size-label subheading text-uppercase">EU</span>
              <span class="size-label subheading text-uppercase">US</span>
              <span class="size-label subheading text-uppercase">UK</span>
            </div>
          </div>
        `;
      }

      this.content.innerHTML = `
        <div class="size-select">
          ${conversionHeader}
          <div class="size-select-grid">
            ${optionsContent.outerHTML}
          </div>
        </div>
      `;

      const modalInputs = this.content.querySelectorAll('.variant-option__input');
      modalInputs.forEach((modalInput) => {
        if (modalInput.checked) {
          modalInput.setAttribute('checked', '');
        } else {
          modalInput.removeAttribute('checked');
        }
      });

      if (this.sizeConversionEnabled) {
        this.applySizeConversion();
      }

      this.addSizeSelectHandlers();
    }

    isolateModalVariantInputs(rootElement = this.content) {
      if (!rootElement) {
        return;
      }

      const modalInputs = rootElement.querySelectorAll('.variant-option__input');
      const modalScope = `size-guide-modal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      modalInputs.forEach((input, index) => {
        const originalId = input.id;
        const isolatedId = `${modalScope}-${index}`;
        const isolatedName = `${modalScope}-option`;

        input.id = isolatedId;
        input.name = isolatedName;
        input.removeAttribute('form');

        if (originalId) {
          input.dataset.originalId = originalId;
        }
        input.dataset.modalValue = input.value;

        const wrapper = input.closest('.variant-option__button-wrapper, .variant-option');
        if (!wrapper) {
          return;
        }

        const label = wrapper.querySelector('.variant-option__label');
        if (label) {
          label.setAttribute('for', isolatedId);
        }
      });
    }

    findSizeFieldset() {
      const sizeOptionName = this.getSizeOptionName();

      const allFieldsets = document.querySelectorAll('.variant-picker__fieldset');

      const sizeFieldset = Array.from(allFieldsets).find((fieldset) => {
        const legend = fieldset.querySelector('.variant-picker__legend');
        if (!legend) {
          return false;
        }

        const legendClone = legend.cloneNode(true);
        const button = legendClone.querySelector('.variant-picker__size-guide-btn');
        if (button) {
          button.remove();
        }
        const legendText = legendClone.textContent.trim();

        return legendText.includes(sizeOptionName);
      });

      return sizeFieldset || null;
    }

    getSizeOption() {
      const sizeOptionName = this.getSizeOptionName();

      const allFieldsets = document.querySelectorAll('.variant-picker__fieldset');

      const sizeFieldset = Array.from(allFieldsets).find((fieldset) => {
        const legend = fieldset.querySelector('.variant-picker__legend');
        if (!legend) {
          return false;
        }

        const legendClone = legend.cloneNode(true);
        const button = legendClone.querySelector('.variant-picker__size-guide-btn');
        if (button) {
          button.remove();
        }
        const legendText = legendClone.textContent.trim();

        return legendText.includes(sizeOptionName);
      });

      if (!sizeFieldset) {
        return null;
      }

      const sizeInputs = sizeFieldset.querySelectorAll('.variant-option__input[name*="Size"]');
      const currentSelected = sizeFieldset.querySelector('.variant-option__input:checked');

      const sizeValues = Array.from(sizeInputs).map((input) => {
        const value = input.value;
        const isSelected = input.checked;
        const isAvailable = !input.closest('.variant-option__label').querySelector('.variant-option__strikethrough');

        return {
          value,
          available: isAvailable,
          selected: isSelected
        };
      });

      return {
        name: sizeOptionName,
        selected_value: currentSelected ? currentSelected.value : sizeValues[0]?.value,
        values: sizeValues
      };
    }

    getSizeOptionName() {
      if (this.currentSizeOptionName) {
        return this.currentSizeOptionName;
      }

      const variantPicker = document.querySelector('variant-picker');
      if (!variantPicker) {
        return 'Size';
      }

      const commonSizeNames = ['Size', 'size', 'SIZE', 'Shoe Size', 'Clothing Size'];
      const productData = variantPicker.productData;

      if (productData && productData.options) {
        for (const commonName of commonSizeNames) {
          const foundOption = productData.options.find((option) => option.name.toLowerCase() === commonName.toLowerCase());
          if (foundOption) {
            return foundOption.name;
          }
        }
      }

      return 'Size';
    }

    detectProductType() {
      if (this.detectedProductType) {
        return this.detectedProductType;
      }

      const letterSizes = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL', '6XL', '7XL', '8XL', '9XL', '10XL', '11XL', '12XL', '13XL', '14XL', '15XL', '16XL', '17XL', '18XL', '19XL', '20XL', '21XL', '22XL', '23XL', '24XL'];
      
      let hasLetterSize = false;
      let numericSizes = [];
      let sizeValues = [];

      if (this.cardWrapper) {
        const cardVariants = this.cardWrapper.querySelector('card-variants');
        const bundleVariants = this.cardWrapper.querySelector('[data-component="bundle-variants"]');
        const variantsRoot = cardVariants || bundleVariants;

        if (variantsRoot) {
          const groupSelector = cardVariants ? '.card-variants__group' : '.bundle-variants__group';
          const swatchSelector = cardVariants ? '.card-variant-swatch' : '.bundle-variant-swatch';
          const variantGroups = variantsRoot.querySelectorAll(groupSelector);

          variantGroups.forEach((group) => {
            const swatches = group.querySelectorAll(swatchSelector);
            swatches.forEach((swatch) => {
              const optionValue = swatch.dataset.optionValue;
              if (optionValue) {
                sizeValues.push(optionValue.trim());
              }
            });
          });
        }
      }

      if (sizeValues.length === 0) {
        const sizeFieldset = this.findSizeFieldset();
        if (sizeFieldset) {
          const sizeInputs = sizeFieldset.querySelectorAll('.variant-option__input[name*="Size"]');
          sizeInputs.forEach((input) => {
            sizeValues.push(input.value.trim());
          });
        }
      }

      sizeValues.forEach((value) => {
        if (letterSizes.includes(value)) {
          hasLetterSize = true;
        } else {
          const numSize = parseFloat(value);
          if (!Number.isNaN(numSize)) {
            numericSizes.push(numSize);
          }
        }
      });

      if (hasLetterSize) {
        this.detectedProductType = 'clothing';
        return this.detectedProductType;
      }

      if (numericSizes.length > 0) {
        const minSize = Math.min(...numericSizes);
        const maxSize = Math.max(...numericSizes);

        if ((minSize >= 35 && maxSize <= 48) || (minSize >= 4 && maxSize <= 17) || (minSize >= 2.5 && maxSize <= 15.5)) {
          this.detectedProductType = 'shoes';
        } else if (minSize >= 4 && maxSize <= 60) {
          this.detectedProductType = 'clothing';
        } else {
          this.detectedProductType = 'shoes';
        }
      } else {
        this.detectedProductType = 'shoes';
      }

      return this.detectedProductType;
    }

    getEffectiveConversionType() {
      if (this.sizeConversionType === 'auto') {
        return this.detectProductType();
      }
      return this.sizeConversionType;
    }

    convertSize(size) {
      if (!this.sizeConversionEnabled) {
        return { EU: size };
      }

      if (this.sizeConversionType === 'custom') {
        let customRules = null;
        if (this.sizeConversionRules) {
          try {
            customRules = JSON.parse(this.sizeConversionRules);
          } catch (error) {
            customRules = null;
          }
        }

        if (customRules && customRules[size]) {
          const customRule = { ...customRules[size] };
          if (!customRule.EU) {
            customRule.EU = size;
          }
          return customRule;
        }
      }

      const sizeSystem = this.detectSizeSystem(size);

      if (sizeSystem === 'US') {
        return this.convertFromUS(size);
      }
      if (sizeSystem === 'UK') {
        return this.convertFromUK(size);
      }
      return this.convertFromEU(size);
    }

    detectSizeSystem(size) {
      const numSize = parseFloat(size);
      const conversionType = this.getEffectiveConversionType();

      const letterSizes = [
        'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL', '6XL', '7XL', '8XL', '9XL', '10XL', '11XL', '12XL', '13XL', '14XL', '15XL', '16XL', '17XL', '18XL', '19XL', '20XL', '21XL', '22XL', '23XL', '24XL'
      ];
      if (letterSizes.includes(size)) {
        return 'EU';
      }

      if (!Number.isNaN(numSize)) {
        if (conversionType === 'shoes') {
          if (numSize >= 35 && numSize <= 48) {
            return 'EU';
          }
          if (numSize >= 4 && numSize <= 17) {
            return 'US';
          }
          if (numSize >= 2.5 && numSize <= 15.5) {
            return 'UK';
          }
        } else if (conversionType === 'clothing') {
          if (numSize >= 4 && numSize <= 60) {
            return 'UK';
          }
        }
      }

      if (!Number.isNaN(numSize) && conversionType === 'shoes') {
        return 'EU';
      }

      return 'EU';
    }

    convertFromEU(size) {
      const numSize = parseFloat(size);
      const isLetterSize = isNaN(numSize);
      const conversionType = this.getEffectiveConversionType();

      if (conversionType === 'shoes') {
        if (isLetterSize) {
          const clothingMap = {
            XXS: { US: 'XXS', UK: '4' },
            XS: { US: 'XS', UK: '6' },
            S: { US: 'S', UK: '8' },
            M: { US: 'M', UK: '10' },
            L: { US: 'L', UK: '12' },
            XL: { US: 'XL', UK: '14' },
            XXL: { US: 'XXL', UK: '16' },
            XXXL: { US: 'XXXL', UK: '18' },
            '4XL': { US: '4XL', UK: '20' },
            '5XL': { US: '5XL', UK: '22' }
          };
          return clothingMap[size] ? { EU: size, ...clothingMap[size] } : { EU: size };
        }
        
        return {
          EU: size,
          US: (numSize - 31).toString(),
          UK: (numSize - 33.5).toString()
        };
      }
      if (conversionType === 'clothing') {
        const clothingMap = {
          XXS: { US: 'XXS', UK: '4' },
          XS: { US: 'XS', UK: '6' },
          S: { US: 'S', UK: '8' },
          M: { US: 'M', UK: '10' },
          L: { US: 'L', UK: '12' },
          XL: { US: 'XL', UK: '14' },
          XXL: { US: 'XXL', UK: '16' },
          XXXL: { US: 'XXXL', UK: '18' },
          '4XL': { US: '4XL', UK: '20' },
          '5XL': { US: '5XL', UK: '22' }
        };

        return clothingMap[size] ? { EU: size, ...clothingMap[size] } : { EU: size };
      }

      return { EU: size };
    }

    convertFromUS(size) {
      const numSize = parseFloat(size);
      const conversionType = this.getEffectiveConversionType();

      if (conversionType === 'shoes') {
        return {
          EU: (numSize + 31).toString(),
          US: size,
          UK: (numSize - 1.5).toString()
        };
      }
      if (conversionType === 'clothing') {
        const clothingMap = {
          XXS: { EU: 'XXS', UK: '4' },
          XS: { EU: 'XS', UK: '6' },
          S: { EU: 'S', UK: '8' },
          M: { EU: 'M', UK: '10' },
          L: { EU: 'L', UK: '12' },
          XL: { EU: 'XL', UK: '14' },
          XXL: { EU: 'XXL', UK: '16' },
          XXXL: { EU: 'XXXL', UK: '18' },
          '4XL': { EU: '4XL', UK: '20' },
          '5XL': { EU: '5XL', UK: '22' }
        };

        return clothingMap[size] ? { EU: clothingMap[size].EU, US: size, UK: clothingMap[size].UK } : { EU: size, US: size };
      }

      return { EU: size, US: size };
    }

    convertFromUK(size) {
      const numSize = parseFloat(size);
      const conversionType = this.getEffectiveConversionType();

      if (conversionType === 'shoes') {
        return {
          EU: (numSize + 33.5).toString(),
          US: (numSize + 1.5).toString(),
          UK: size
        };
      }
      if (conversionType === 'clothing') {
        const ukToLetterMap = {
          4: { EU: 'XXS', US: 'XXS' },
          6: { EU: 'XS', US: 'XS' },
          8: { EU: 'S', US: 'S' },
          10: { EU: 'M', US: 'M' },
          12: { EU: 'L', US: 'L' },
          14: { EU: 'XL', US: 'XL' },
          16: { EU: 'XXL', US: 'XXL' },
          18: { EU: 'XXXL', US: 'XXXL' },
          20: { EU: '4XL', US: '4XL' },
          22: { EU: '5XL', US: '5XL' }
        };

        return ukToLetterMap[size] ? { EU: ukToLetterMap[size].EU, US: ukToLetterMap[size].US, UK: size } : { EU: size, UK: size };
      }

      return { EU: size, UK: size };
    }

    applySizeConversion() {
      if (!this.content) {
        return;
      }

      const variantLabels = this.content.querySelectorAll('.variant-option__label-text');

      variantLabels.forEach((label) => {
        const originalSize = label.textContent.trim();
        const convertedSize = this.convertSize(originalSize);
        const primarySystem = this.detectSizeSystem(originalSize);

        let conversionHTML = '';

        if (convertedSize.EU && convertedSize.US && convertedSize.UK) {
          conversionHTML = `
            <div class="size-conversion-row subheading text-uppercase">
              <span class="size-eu ${primarySystem === 'EU' ? 'size-primary' : ''}">${convertedSize.EU}</span>
              <span class="size-us ${primarySystem === 'US' ? 'size-primary' : ''}">${convertedSize.US}</span>
              <span class="size-uk ${primarySystem === 'UK' ? 'size-primary' : ''}">${convertedSize.UK}</span>
            </div>
          `;
        } else if (convertedSize.EU && convertedSize.US) {
          conversionHTML = `
            <div class="size-conversion-row subheading text-uppercase">
              <span class="size-eu ${primarySystem === 'EU' ? 'size-primary' : ''}">${convertedSize.EU}</span>
              <span class="size-us ${primarySystem === 'US' ? 'size-primary' : ''}">${convertedSize.US}</span>
              <span class="size-uk">-</span>
            </div>
          `;
        } else if (convertedSize.EU && convertedSize.UK) {
          conversionHTML = `
            <div class="size-conversion-row subheading text-uppercase">
              <span class="size-eu ${primarySystem === 'EU' ? 'size-primary' : ''}">${convertedSize.EU}</span>
              <span class="size-us">-</span>
              <span class="size-uk ${primarySystem === 'UK' ? 'size-primary' : ''}">${convertedSize.UK}</span>
            </div>
          `;
        } else if (convertedSize.US && convertedSize.UK) {
          conversionHTML = `
            <div class="size-conversion-row subheading text-uppercase">
              <span class="size-eu">-</span>
              <span class="size-us ${primarySystem === 'US' ? 'size-primary' : ''}">${convertedSize.US}</span>
              <span class="size-uk ${primarySystem === 'UK' ? 'size-primary' : ''}">${convertedSize.UK}</span>
            </div>
          `;
        }

        if (conversionHTML) {
          label.innerHTML = conversionHTML;
        }
      });
    }

    loadCardSizeSelect(cardWrapper, sizeOptionName, productId) {
      if (!this.content) {
        return;
      }

      const content = this.content;

      const cardVariants = cardWrapper.querySelector('card-variants');
      const bundleVariants = cardWrapper.querySelector('[data-component="bundle-variants"]');
      const variantsRoot = cardVariants || bundleVariants;

      if (!variantsRoot) {
        content.innerHTML = '<div class="size-select"><p>No variants found for this product.</p></div>';
        return;
      }

      const groupSelector = cardVariants ? '.card-variants__group' : '.bundle-variants__group';
      const swatchSelector = cardVariants ? '.card-variant-swatch' : '.bundle-variant-swatch';
      const swatchContainerSelector = cardVariants ? '.card-variants__swatches' : '.bundle-variants__swatches';
      const selectedClass = cardVariants ? 'card-variant-swatch--selected' : 'bundle-variant-swatch--selected';
      const disabledClass = cardVariants ? 'card-variant-swatch--disabled' : 'bundle-variant-swatch--disabled';

      const variantGroups = variantsRoot.querySelectorAll(groupSelector);
      let targetGroup = null;

      variantGroups.forEach((group) => {
        const swatches = group.querySelectorAll(swatchSelector);
        if (swatches.length > 0) {
          const firstSwatch = swatches[0];
          const optionType = firstSwatch.dataset.optionType;
          if (optionType && optionType.toLowerCase() === sizeOptionName.toLowerCase()) {
            targetGroup = group;
          }
        }
      });

      if (!targetGroup) {
        content.innerHTML = '<div class="size-select"><p>No size options found for this product.</p></div>';
        return;
      }

      const swatchesContainer = targetGroup.querySelector(swatchContainerSelector);
      if (!swatchesContainer) {
        content.innerHTML = '<div class="size-select"><p>No size options container found.</p></div>';
        return;
      }

      const clonedSwatches = swatchesContainer.cloneNode(true);

      const swatchButtons = clonedSwatches.querySelectorAll(swatchSelector);
      const optionsHTML = Array.from(swatchButtons)
        .map((swatch) => {
          const value = swatch.dataset.optionValue;
          const isSelected = swatch.classList.contains(selectedClass);
          const isDisabled = swatch.classList.contains(disabledClass);

          const checkedAttr = isSelected ? 'checked' : '';
          const disabledStateClass = isDisabled ? 'variant-option--disabled' : '';

          return `
            <div class="variant-option ${disabledStateClass}">
              <input 
                type="radio" 
                name="card-size-select-${productId}" 
                value="${value}" 
                class="variant-option__input"
                id="card-size-${productId}-${value.replace(/[^a-zA-Z0-9]/g, '-')}"
                tabindex="0"
                ${checkedAttr}
                ${isDisabled ? 'disabled' : ''}
              />
              <label 
                for="card-size-${productId}-${value.replace(/[^a-zA-Z0-9]/g, '-')}" 
                class="variant-option__label"
                tabindex="0"
              >
                <span class="variant-option__label-text">${value}</span>
                ${isDisabled ? '<span class="variant-option__strikethrough"></span>' : ''}
              </label>
            </div>
          `;
        })
        .join('');

      content.innerHTML = `
        <div class="size-select size-select--card-variants">
          <div class="size-select-grid">
            <div class="variant-picker__options variant-picker__options--button">
              ${optionsHTML}
            </div>
          </div>
        </div>
      `;

      if (this.sizeConversionEnabled) {
        this.applySizeConversion();
      }

      this.addCardSizeSelectHandlers(cardWrapper, productId);
    }

    addCardSizeSelectHandlers(cardWrapper, productId) {
      if (!this.content) {
        return;
      }

      const modalInputs = this.content.querySelectorAll('.variant-option__input');
      const variantLabels = this.content.querySelectorAll('.variant-option__label');
      const cardVariants = cardWrapper.querySelector('card-variants');
      const bundleVariants = cardWrapper.querySelector('[data-component="bundle-variants"]');
      const variantsRoot = cardVariants || bundleVariants;
      const swatchSelector = cardVariants ? '.card-variant-swatch' : '.bundle-variant-swatch';

      const handleVariantClick = (clickedInput) => {
        if (!clickedInput) {
          return;
        }

        if (clickedInput.disabled) {
          return;
        }

        const selectedValue = clickedInput.value;

        if (variantsRoot) {
          const cardSwatches = variantsRoot.querySelectorAll(swatchSelector);
          cardSwatches.forEach((swatch) => {
            if (swatch.dataset.optionValue === selectedValue) {
              swatch.click();
            }
          });
        }

        modalInputs.forEach((input) => {
          input.checked = input === clickedInput;
          if (input === clickedInput) {
            input.setAttribute('checked', '');
          } else {
            input.removeAttribute('checked');
          }
        });

        if (this.addToBundleAfterSelection) {
          this.triggerBundleAddAfterSelection(cardWrapper);
        } else if (this.addToCartAfterSelection) {
          this.triggerCardVariantAddToCart(cardWrapper);
        }
      };

      modalInputs.forEach((modalInput) => {
        modalInput.addEventListener('click', (e) => {
          e.stopPropagation();
          handleVariantClick(modalInput);
        });
      });

      variantLabels.forEach((label) => {
        label.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') {
            return;
          }

          e.preventDefault();
          label.click();
        });

        label.addEventListener('click', (e) => {
          const inputId = label.getAttribute('for');
          const modalInput = inputId
            ? this.content.querySelector(`#${inputId}`)
            : label.querySelector('.variant-option__input');

          if (!modalInput) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();

          if (modalInput.disabled) {
            return;
          }

          modalInput.checked = true;
          handleVariantClick(modalInput);
        });
      });
    }

    triggerCardVariantAddToCart(cardWrapper) {
      if (!cardWrapper || this.isAddingToCartAfterSelection) {
        return;
      }

      const productFormElement = cardWrapper.querySelector('product-form');
      const form = productFormElement?.querySelector('form');
      const submitButton = form?.querySelector('[type="submit"]');
      const variantId = form?.querySelector('[name="id"]')?.value;

      if (!form || !submitButton) {
        this.fallbackToProductPage(cardWrapper);
        return;
      }

      if (
        variantId &&
        productFormElement?.dataset.cartAddErrorVariantId &&
        productFormElement.dataset.cartAddErrorVariantId === variantId
      ) {
        this.showUnavailableMessage(productFormElement.dataset.cartAddErrorMessage);
        return;
      }

      if (submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') {
        return;
      }

      this.isAddingToCartAfterSelection = true;
      this.setAddToCartLoading(true);

      let timeoutId;
      const cleanup = () => {
        productFormElement?.removeEventListener('product-form:added', handleSuccess);
        productFormElement?.removeEventListener('product-form:error', handleError);
        window.clearTimeout(timeoutId);
        this.isAddingToCartAfterSelection = false;
        this.setAddToCartLoading(false);
      };

      const handleSuccess = () => {
        cleanup();
        window.setTimeout(() => {
          this.close();
        }, this.closeAfterSelectionDelayMs);
      };

      const handleError = (event) => {
        cleanup();
        this.showUnavailableMessage(event?.detail?.response || productFormElement?.dataset.cartAddErrorMessage);
      };

      productFormElement?.addEventListener('product-form:added', handleSuccess, { once: true });
      productFormElement?.addEventListener('product-form:error', handleError, { once: true });

      timeoutId = window.setTimeout(cleanup, 5000);

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitButton);
      } else {
        submitButton.click();
      }
    }

    setAddToCartLoading(isLoading) {
      if (!this.modal) {
        return;
      }

      this.modal.classList.toggle('size-guide-modal--adding-to-cart', isLoading);
      this.modal.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    showUnavailableMessage(messageSource) {
      if (!this.content) {
        return;
      }

      const existing = this.content.querySelector('.size-select__message');
      if (existing) {
        existing.remove();
      }

      const message = document.createElement('p');
      message.className = 'size-select__message caption';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      message.textContent = this.getUnavailableMessage(messageSource);

      const sizeSelect = this.content.querySelector('.size-select');
      (sizeSelect || this.content).appendChild(message);

      window.setTimeout(() => {
        message.remove();
      }, 5000);
    }

    getUnavailableMessage(messageSource) {
      if (typeof messageSource === 'string' && messageSource.trim()) {
        return messageSource;
      }

      if (messageSource && typeof messageSource.description === 'string') {
        return messageSource.description;
      }

      if (messageSource && typeof messageSource.message === 'string') {
        return messageSource.message;
      }

      return window.variantStrings?.soldOut || window.variantStrings?.unavailable || 'This variant is unavailable.';
    }

    triggerBundleAddAfterSelection(cardWrapper) {
      if (!cardWrapper || this.isAddingToCartAfterSelection) {
        return;
      }

      const addButtonWrapper = cardWrapper.querySelector('[data-component="bundle-add-button"]');
      const addButton = addButtonWrapper?.querySelector('button');

      if (!addButton) {
        this.fallbackToProductPage(cardWrapper);
        return;
      }

      const isDisabled =
        addButton.disabled ||
        addButton.getAttribute('aria-disabled') === 'true' ||
        addButtonWrapper.classList.contains('bundle-add-button--disabled');

      if (isDisabled) {
        return;
      }

      this.isAddingToCartAfterSelection = true;
      this.setAddToCartLoading(true);

      const bundleProductForm = cardWrapper.querySelector('product-form');

      let timeoutId;
      const cleanup = () => {
        bundleProductForm?.removeEventListener('product-form:added', handleSuccess);
        bundleProductForm?.removeEventListener('product-form:error', handleError);
        window.clearTimeout(timeoutId);
        this.isAddingToCartAfterSelection = false;
        this.setAddToCartLoading(false);
      };

      const handleSuccess = () => {
        cleanup();
        window.setTimeout(() => {
          this.close();
        }, this.closeAfterSelectionDelayMs);
      };

      const handleError = (event) => {
        cleanup();
        this.showUnavailableMessage(event?.detail?.response || bundleProductForm?.dataset.cartAddErrorMessage);
      };

      if (bundleProductForm) {
        bundleProductForm.addEventListener('product-form:added', handleSuccess, { once: true });
        bundleProductForm.addEventListener('product-form:error', handleError, { once: true });
        timeoutId = window.setTimeout(cleanup, 5000);
      } else {
        timeoutId = window.setTimeout(() => {
          this.isAddingToCartAfterSelection = false;
          this.setAddToCartLoading(false);
        }, this.closeAfterSelectionDelayMs + 500);
      }

      addButton.click();

      if (!bundleProductForm) {
        window.setTimeout(() => {
          this.close();
        }, this.closeAfterSelectionDelayMs);
      }
    }

    fallbackToProductPage(cardWrapper) {
      if (!cardWrapper) {
        return;
      }

      const cardVariants = cardWrapper.querySelector('card-variants');
      const variantInput = cardVariants?.querySelector('input[name="id"]');
      const variantId = variantInput?.value;

      const productLink = cardWrapper.querySelector('a[href*="/products/"]');
      const productHref = productLink?.getAttribute('href');

      if (!productHref) {
        this.close();
        return;
      }

      try {
        const url = new URL(productHref, window.location.origin);
        if (variantId) {
          url.searchParams.set('variant', variantId);
        }
        window.location.href = url.pathname + url.search;
      } catch (_error) {
        this.close();
      }
    }

    addSizeSelectHandlers() {
      if (!this.content) {
        return;
      }

      if (!this.findSizeFieldset()) {
        return;
      }

      const variantInputs = this.content.querySelectorAll('.variant-option__input');
      const variantLabels = this.content.querySelectorAll('.variant-option__label');

      const handleVariantClick = (clickedInput) => {
        const liveSizeFieldset = this.findSizeFieldset();
        if (!liveSizeFieldset) {
          return;
        }

        const originalInput = liveSizeFieldset.querySelector(`.variant-option__input[value="${clickedInput.value}"]`);
        if (originalInput) {
          originalInput.checked = true;
          originalInput.dispatchEvent(new Event('change', { bubbles: true }));

          variantInputs.forEach((input) => {
            input.checked = input === clickedInput;
            if (input === clickedInput) {
              input.setAttribute('checked', '');
            } else {
              input.removeAttribute('checked');
            }
          });
        }
      };

      variantInputs.forEach((modalInput) => {
        modalInput.addEventListener('click', (e) => {
          e.stopPropagation();
          handleVariantClick(modalInput);
        });
      });

      variantLabels.forEach((label) => {
        const inputId = label.getAttribute('for');
        const modalInput = inputId ? this.content.querySelector(`#${inputId}`) : label.querySelector('.variant-option__input');

        if (modalInput && !modalInput.disabled) {
          label.setAttribute('tabindex', '0');
        } else {
          label.setAttribute('tabindex', '-1');
          label.setAttribute('aria-disabled', 'true');
        }

        label.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') {
            return;
          }

          e.preventDefault();
          label.click();
        });

        label.addEventListener('click', (e) => {
          if (inputId) {
            if (modalInput) {
              e.preventDefault();
              e.stopPropagation();
              modalInput.checked = true;
              handleVariantClick(modalInput);
            }
          } else {
            const modalInput = label.querySelector('.variant-option__input');
            if (modalInput) {
              e.preventDefault();
              e.stopPropagation();
              modalInput.checked = true;
              handleVariantClick(modalInput);
            }
          }
        });
      });
    }

    getFocusableElements() {
      if (!this.modal) {
        return [];
      }

      return Array.from(
        this.modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
    }

    trapFocus(event) {
      if (!this.modal) {
        return;
      }

      const focusableElements = this.getFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        this.modal.focus();
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      const currentActive = document.activeElement;
      const isOutside = !this.modal.contains(currentActive);
      const moveToLast = event.shiftKey && (currentActive === firstFocusable || isOutside);
      const moveToFirst = !event.shiftKey && (currentActive === lastFocusable || isOutside);

      if (moveToLast) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (moveToFirst) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }

    open() {
      if (!this.modal) {
        return;
      }
      this.modal.setAttribute('aria-hidden', 'false');
      if (!this.modal.hasAttribute('tabindex')) {
        this.modal.setAttribute('tabindex', '-1');
      }
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        const target =
          this.modal.querySelector('.size-guide-modal__content .variant-option__input:checked + .variant-option__label') ||
          this.modal.querySelector('.size-guide-modal__content .variant-option__label') ||
          this.getFocusableElements()[0];

        (target || this.modal).focus();
      });
    }

    close() {
      if (!this.modal) {
        return;
      }

      this.modal.setAttribute('aria-hidden', 'true');
      this.setAddToCartLoading(false);
      this.modal.classList.remove('size-guide-modal--card-variants');
      document.body.style.overflow = '';

      const canFocusLastElement = this.lastFocusedElement && document.contains(this.lastFocusedElement);
      const fallbackTrigger =
        this.lastTriggerSelector ? document.querySelector(this.lastTriggerSelector) : null;
      const focusTarget = canFocusLastElement ? this.lastFocusedElement : fallbackTrigger;

      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({ preventScroll: true });
      }
      this.lastFocusedElement = null;
      this.lastTriggerSelector = null;
      this.addToCartAfterSelection = false;
      this.addToBundleAfterSelection = false;
    }

    isOpen() {
      if (!this.modal) {
        return false;
      }

      return this.modal.getAttribute('aria-hidden') === 'false';
    }
  }

  window[GLOBAL_CLASS_KEY] = SizeGuideModal;

  const initialize = () => {
    initInstance();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();